import { BPFormProvider, Input } from '@bpba/form-components';
import { Button, Span, VStack } from '@bpba/ui-components';
import { useForm } from 'react-hook-form';
import { FixedTermSimulatorRules } from './FixedTermSimulatorRules';
import { UrlsBack, UrlsFront } from '../constants/urls';
import useAxios from 'axios-hooks';
import { IFixedTermSimulation } from '../types/IFixedTermSimulation';
import { useNavigate } from 'react-router-dom';
import { IFixedTermSimulationResult } from '../types/IFixedTermSimulationResult';
import { renderAmount } from './RenderAmount';
import { renderRate } from './RenderRate';
import { IFixedTermItem } from '../types/IFixedTermItem';
import { useEffect, useState } from 'react';
import { useMenuContext } from '../components/useMenuContext';
import { Titulo } from '../components/shared/Titulo';
import { SubTitulo } from '../components/shared/SubTitulo';
import { Spinner } from '../components/shared/Spinner';
import { useScrolltoContent } from '../components/shared/useScrolltoContent';
import { ErrorMessageContainer } from '../components/shared/ErrorMessageContainer';
import { useErrorMessage } from '../components/shared/useErrorMessage';
import { axiosMessageExtractor } from '../config/axiosMessageExtractor';
import { InputAmount } from './InputAmount';
import { PanelDivisor } from '../components/shared/PanelDivisor';
import { StyledBox } from '../components/shared/StyledBox';
import { renderDate } from './RenderDate';
import { SimulatorWarning } from './SimulatorWarning';
import { SIMULATOR_INFO_TEXT, MENU_ITEM_NEW } from '../constants/data';
import { FaInfoCircle } from 'react-icons/fa';

export const FixedTermSimulatorForm = () => {
  const { scrollToTop } = useScrolltoContent();
  const { setOption } = useMenuContext();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setOption(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useForm<IFixedTermSimulation>();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage, showError, clearError] = useErrorMessage(axiosMessageExtractor);

  const [{ data: simulation }, postSimulation] = useAxios<IFixedTermSimulationResult, IFixedTermSimulation>(
    { url: UrlsBack.FixedTermSimulation, method: 'POST' },
    { manual: true }
  );

  const darAltaOnClick = (data: IFixedTermSimulation) => {
    if (simulation) {
      const state: IFixedTermItem = {
        id: '0',
        tna: simulation.rate,
        tae: simulation.tae,
        interest: simulation.interestAmount,
        finalAmount: simulation.finalAmount,
        expiredDate: simulation.expirationDate,
        createdDate: simulation.issueDate,
        term: Number(simulation?.term),
        amount: Number(data?.amount?.toString()?.replace(',', '.')),
      };

      navigate(UrlsFront.FixedTermNew, {
        state: state,
      });
    }
  };

  const onCancel = () => {
    clearError();
    navigate(UrlsFront.FixedTerms);
  };

  const simularOnClick = async (data: IFixedTermSimulation) => {
    scrollToTop(350);
    setLoading(true);
    clearError();

    await postSimulation({
      data: {
        amount: Number(data.amount?.toString()?.replace(',', '.')),
        term: Number(data.term),
      },
    }).catch(setErrorMessage);

    setLoading(false);
  };

  return (
    <VStack>
      <Titulo>{MENU_ITEM_NEW}</Titulo>
      <PanelDivisor flexDirection={'column'}>
        <SubTitulo>Simulador</SubTitulo>
        <StyledBox gap={'8px'} flexDirection={'row'} alignItems={'center'}>
          <FaInfoCircle />
          <Span fontSize={'12px'}>{SIMULATOR_INFO_TEXT}</Span>
        </StyledBox>
        <BPFormProvider methods={methods} rules={FixedTermSimulatorRules}>
          <VStack as="form" onSubmit={methods.handleSubmit(simulation ? darAltaOnClick : simularOnClick)}>
            <StyledBox flexDirection={['column', 'row']}>
              <InputAmount form={methods} readOnly={simulation != null} />
              <Input readOnly={simulation != null} name="term" control={methods.control} label="Ingresá plazo (días)" placeholder="Plazo (ej: 30 días)" />{' '}
            </StyledBox>
            {loading && !simulation && <Spinner width={40} my={'48px'} />}
            {simulation && (
              <VStack mt={'24px'}>
                <SubTitulo>Resultado</SubTitulo>
                <VStack>
                  <StyledBox
                    flexDirection={['column', 'row']}
                    my={'32px'}
                    fontSize={'20px'}
                    color={'primary.0'}
                    fontWeight={600}
                    alignItems={'center'}
                    justifyContent={'space-evenly'}
                  >
                    <VStack alignItems={'center'} width={'fit-content'}>
                      <Span mb={'8px'}>Interés que ganás</Span> {renderAmount(simulation.interestAmount)}
                    </VStack>
                    <VStack alignItems={'center'} width={'fit-content'} mt={['16px', '0px']}>
                      <Span mb={'8px'}>Total a acreditar</Span> {renderAmount(simulation.finalAmount)}
                    </VStack>
                  </StyledBox>
                  <VStack justifyContent={'center'} my={'36px'} fontWeight={600} color={'primary.0'}>
                    <StyledBox gap="4px">
                      <Span>Período</Span>
                      {renderDate(simulation.issueDate)} - {renderDate(simulation.expirationDate)} ({simulation.term} días)
                    </StyledBox>
                    <StyledBox gap="4px" mt={'8px'}>
                      <Span>TNA</Span>
                      {renderRate(simulation.rate)}
                    </StyledBox>
                  </VStack>
                </VStack>
              </VStack>
            )}

            <ErrorMessageContainer show={showError} errorMessage={errorMessage} />
            <SimulatorWarning />

            {!loading && (
              <StyledBox justifyContent={'end'} mt={'24px'}>
                <Button height={'fit-content'} type="button" variant="transparent" onClick={onCancel} mx={'0px !important '} maxWidth={'200px'}>
                  Cancelar
                </Button>
                <Button height={'fit-content'} type="submit" variant={'secondary'} mx={'0px !important'} maxWidth={'200px'}>
                  {simulation ? 'Confirmar' : 'Simular'}
                </Button>
              </StyledBox>
            )}
          </VStack>
        </BPFormProvider>
      </PanelDivisor>
    </VStack>
  );
};
